import { useEffect, useState, useMemo } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/mocks/blog';
import { categoryToSlug, getAllCategories, isNewPost } from '@/utils/blogUtils';
import BlogSearchBar from './components/BlogSearchBar';
import ReadTimeIndicator from './components/ReadTimeIndicator';
import BlogCard from './components/BlogCard';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useCardStagger } from '@/hooks/useCardStagger';

export default function BlogPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return blogPosts;
    return blogPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        post.category.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  const maxReadMinutes = useMemo(
    () => Math.max(...blogPosts.map((p) => parseInt(p.readTime, 10) || 0)),
    [],
  );

  /* Hero animation */
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });

  /* Featured post (first result) */
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation({ threshold: 0.1 });

  /* Card stagger — resets when filter changes */
  const {
    containerRef: gridRef,
    visibleCount,
  } = useCardStagger(filteredPosts.length, {
    staggerMs: 65,
    resetKey: searchQuery,
  });

  /* SEO */
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    const blogSchema = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${siteUrl}/blog#blog`,
      name: 'Blog STIB - Insights sobre Tecnologia de Áudio e Eventos',
      description: 'Conteúdos exclusivos sobre tecnologia de áudio, palestra silenciosa, tradução simultânea e eventos corporativos em São Paulo.',
      url: `${siteUrl}/blog`,
      inLanguage: 'pt-BR',
      publisher: {
        '@type': 'Organization',
        name: 'STIB',
        logo: {
          '@type': 'ImageObject',
          url: '/img/stib/logo-stib-3d.png',
        },
      },
      blogPost: blogPosts.map((post, index) => ({
        '@type': 'BlogPosting',
        '@id': `${siteUrl}/blog/${post.slug}#article`,
        headline: post.title,
        description: post.excerpt,
        image: post.image,
        datePublished: post.date,
        author: { '@type': 'Organization', name: 'STIB' },
        publisher: {
          '@type': 'Organization',
          name: 'STIB',
          logo: {
            '@type': 'ImageObject',
            url: '/img/stib/logo-stib-3d.png',
          },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/blog/${post.slug}` },
        articleSection: post.category,
        position: index + 1,
      })),
    };

    const itemListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: blogPosts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${siteUrl}/blog/${post.slug}`,
        name: post.title,
      })),
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
      ],
    };

    document.title = 'Blog | STIB - Insights sobre Tecnologia de Áudio e Eventos';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Conteúdos exclusivos sobre tecnologia de áudio, palestra silenciosa, tradução simultânea e eventos corporativos em São Paulo. Dicas e tendências do setor.',
    );

    const scripts = [
      { id: 'blog-schema', data: blogSchema },
      { id: 'itemlist-schema', data: itemListSchema },
      { id: 'breadcrumb-blog-schema', data: breadcrumbSchema },
    ];
    scripts.forEach(({ id, data }) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      el.text = JSON.stringify(data);
      document.head.appendChild(el);
    });
    return () => scripts.forEach(({ id }) => document.getElementById(id)?.remove());
  }, []);

  const gridPosts = filteredPosts.slice(searchQuery ? 0 : 1);
  const featuredPost = !searchQuery ? filteredPosts[0] : null;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section
        ref={heroRef}
        className="relative h-[460px] flex items-center justify-center overflow-hidden mt-[72px]"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/congresso-workshop.webp"
            alt="Blog & Insights"
            className={`w-full h-full object-cover object-top transition-all duration-1000 ${heroVisible ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-secondary/70 via-secondary/50 to-secondary/70"></div>
        </div>

        <div className="absolute top-6 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb variant="light" items={[{ label: 'Home', to: '/' }, { label: 'Blog' }]} />
          </div>
        </div>

        <div className={`relative z-10 text-center px-6 w-full transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className={`font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {blogPosts.length} artigos publicados
          </p>
          <h1 className={`font-heading font-bold text-6xl md:text-7xl text-white mb-4 transition-all duration-700 delay-150 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            BLOG &amp; INSIGHTS
          </h1>
          <p className={`font-body text-lg text-white/85 mb-8 transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            Conteúdos exclusivos sobre tecnologia de áudio e eventos corporativos
          </p>
          <div className={`transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <BlogSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              totalResults={filteredPosts.length}
              totalPosts={blogPosts.length}
            />
          </div>
        </div>
      </section>

      {/* ── Category filter pills ── */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <button
              onClick={() => setSearchQuery('')}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full font-body text-xs font-semibold bg-secondary text-white whitespace-nowrap cursor-pointer"
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-apps-2-line text-xs"></i>
              </div>
              Todos ({blogPosts.length})
            </button>
            {getAllCategories().map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setSearchQuery(cat.name)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full font-body text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                  searchQuery.toLowerCase() === cat.name.toLowerCase()
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-gray-200 text-gray-500 hover:border-primary hover:text-primary'
                }`}
              >
                {cat.name}
                <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full font-bold">
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Posts ── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">

          {/* Search results label */}
          {searchQuery.trim() && (
            <div className="flex items-center justify-between mb-8">
              <p className="font-body text-sm text-gray-500">
                {filteredPosts.length > 0 ? (
                  <>
                    <strong className="text-secondary">{filteredPosts.length}</strong>{' '}
                    {filteredPosts.length === 1 ? 'resultado' : 'resultados'} para{' '}
                    <strong className="text-secondary">&ldquo;{searchQuery}&rdquo;</strong>
                  </>
                ) : (
                  <>
                    Nenhum resultado para{' '}
                    <strong className="text-secondary">&ldquo;{searchQuery}&rdquo;</strong>
                  </>
                )}
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="flex items-center gap-1.5 text-xs font-body text-gray-400 hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-close-line text-xs"></i>
                </div>
                Limpar busca
              </button>
            </div>
          )}

          {filteredPosts.length > 0 ? (
            <>
              {/* Featured post (only when not searching) */}
              {featuredPost && (
                <div
                  ref={featuredRef}
                  className={`mb-10 transition-all duration-700 ${featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                  <Link to={`/blog/${featuredPost.slug}`} className="group cursor-pointer block">
                    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/25 hover:-translate-y-1 transition-all duration-300 grid grid-cols-1 md:grid-cols-2">
                      <div className="relative h-72 md:h-auto overflow-hidden">
                        <img
                          src={featuredPost.image}
                          alt={featuredPost.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="eager"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                        <Link
                          to={`/blog/categoria/${categoryToSlug(featuredPost.category)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full font-body text-xs font-bold uppercase whitespace-nowrap cursor-pointer hover:bg-primary/90"
                        >
                          {featuredPost.category}
                        </Link>
                        <span className="absolute top-4 right-4 bg-white/90 text-secondary px-3 py-1 rounded-full font-body text-xs font-semibold">
                          Destaque
                        </span>
                        {isNewPost(featuredPost.date) && (
                          <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-emerald-500 text-white pl-2 pr-2.5 py-1 rounded-full font-body text-[10px] font-bold uppercase tracking-wide">
                            <span className="relative flex w-1.5 h-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70"></span>
                              <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-white"></span>
                            </span>
                            Novo
                          </div>
                        )}
                      </div>
                      <div className="p-8 md:p-10 flex flex-col justify-center">
                        <p className="font-body text-xs text-gray-400 mb-3">{featuredPost.date}</p>
                        <h2 className="font-heading font-bold text-2xl md:text-3xl text-secondary group-hover:text-primary transition-colors mb-4 leading-snug">
                          {featuredPost.title}
                        </h2>
                        <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 line-clamp-3">
                          {featuredPost.excerpt}
                        </p>
                        <ReadTimeIndicator readTime={featuredPost.readTime} maxMinutes={maxReadMinutes} />
                        <div className="mt-5 flex items-center gap-1.5 font-body text-sm font-semibold text-primary">
                          Ler artigo completo
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform text-sm"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Grid de posts com stagger */}
              <div
                ref={gridRef}
                className="grid grid-cols-1 md:grid-cols-3 gap-7"
              >
                {gridPosts.map((post, index) => (
                  <BlogCard
                    key={post.id}
                    post={post}
                    index={index}
                    isVisible={index < visibleCount}
                    maxReadMinutes={maxReadMinutes}
                  />
                ))}
              </div>
            </>
          ) : (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-100 mb-6">
                <i className="ri-search-eye-line text-4xl text-gray-300"></i>
              </div>
              <h3 className="font-heading font-bold text-2xl text-secondary mb-3">
                Nenhum artigo encontrado
              </h3>
              <p className="font-body text-sm text-gray-500 max-w-sm mb-8">
                Não encontramos artigos para{' '}
                <strong>&ldquo;{searchQuery}&rdquo;</strong>. Tente um termo diferente.
              </p>
              <div className="flex flex-wrap gap-2 justify-center mb-6">
                {getAllCategories().map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setSearchQuery(cat.name)}
                    className="px-4 py-2 rounded-full border border-gray-200 text-sm font-body text-gray-600 hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setSearchQuery('')}
                className="flex items-center gap-2 bg-secondary text-white px-6 py-3 rounded-full font-body text-sm font-semibold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                Ver todos os artigos
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}